import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ManualSolicitationPayload } from '@/lib/types/apiTypes';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import ManualSolicitationImage from '@/components/ui/icons/new/ManualSolicitationImage';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Textarea } from '@/components/ui/textarea';
import DatePicker from '@/components/ui/shared/DatePicker/DatePicker';
import { FC, ReactNode, useEffect } from 'react';
import { convertTimestamp } from '@/lib/functions/funcUtils';

export interface FormData {
	title: string;
	fundingAmount: string;
	deadline: string;
	summary: string;
	agency: string;
}

const formSchema = z.object({
	title: z.string().min(2, {
		message: 'Please enter program title.',
	}),
	fundingAmount: z.string().optional(),
	deadline: z.string().optional(),
	summary: z.string().min(2, {
		message: 'Please enter summary.',
	}),
	agency: z.string().optional(),
});

interface ManualSolicitationSummaryProps {
	type: 'dialog' | 'drawer';
	controlLayout: ReactNode;
	onSubmit: (formData: FormData) => void;
	showView?: boolean;
	solicitationSummary?: ManualSolicitationPayload | null;
}

const ManualSolicitationSummary: FC<ManualSolicitationSummaryProps> = ({
	type,
	controlLayout,
	onSubmit,
	showView,
	solicitationSummary,
}) => {
	const form = useForm<FormData>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			title: '',
			fundingAmount: '',
			deadline: '',
			summary: '',
			agency: '',
		},
	});

	const renderText = (field) => (
		<p className="text-black whitespace-pre-line">
			{field.value ? field.value : '- -'}
		</p>
	);

	const renderInput = (field, placeholder) => {
		return (
			<>
				{showView === true ? (
					<>{renderText(field)}</>
				) : (
					<Input
						className={cn(
							'bg-white border-[#D3DDE2] h-12 p-4 rounded-lg hover:bg-[#EAF1FC] disabled:bg-[#F5F5F5]'
						)}
						placeholder={placeholder}
						{...field}
					></Input>
				)}
			</>
		);
	};

	const setFormValues = (solicitationSummary: ManualSolicitationPayload) => {
		form.setValue('title', solicitationSummary.name);
		form.setValue('fundingAmount', solicitationSummary.metaData.funding || '');
		form.setValue('deadline', solicitationSummary.metaData.deadline || '');
		form.setValue('agency', solicitationSummary.metaData.agency || '');
		form.setValue('summary', solicitationSummary.summary || '');
	};

	useEffect(() => {
		if (solicitationSummary) {
			setFormValues(solicitationSummary);
		}
	}, [solicitationSummary]);

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)}>
				<Card
					className={cn(
						'flex flex-col border-none shadow-none gap-3 2xl:gap-4',
						type === 'dialog' ? 'bg-white' : ''
					)}
				>
					<CardHeader className="flex flex-row gap-10 p-0 text-left">
						{type === 'dialog' && <ManualSolicitationImage />}
						<div className="grid w-full grid-cols-12 ">
							{type === 'dialog' && (
								<h1 className="col-span-12 font-bold text-left text-black dynamic-xl-large">
									Lets get this project set up
								</h1>
							)}

							<FormField
								control={form.control}
								name="title"
								render={({ field }) => {
									return (
										<FormItem className="col-span-12">
											<Label className="font-bold">
												Program Title{' '}
												<span className="text-secondary">(required)</span>
											</Label>
											<FormControl>
												{renderInput(field, 'Enter title here')}
											</FormControl>
											<FormMessage />
										</FormItem>
									);
								}}
							/>
						</div>
					</CardHeader>
					<CardContent className="flex flex-col gap-3 p-0 2xl:gap-4">
						<div
							className={cn(
								'grid gap-3 2xl:gap-4',
								type === 'dialog'
									? 'grid-cols-12'
									: 'grid-cols-6 border-[#D3DDE2] border-y py-3 2xl:py-4'
							)}
						>
							<div className="grid col-span-6">
								<FormField
									control={form.control}
									name="fundingAmount"
									render={({ field }) => {
										return (
											<FormItem>
												<Label className="font-bold">Funding Amount</Label>
												<FormControl>
													{renderInput(
														field,
														'Enter the amount of funding sought'
													)}
												</FormControl>
											</FormItem>
										);
									}}
								/>
							</div>
							<div className="grid col-span-6">
								<FormField
									control={form.control}
									name="deadline"
									render={({ field }) => {
										return (
											<FormItem>
												<Label className="font-bold">Deadline</Label>
												<FormControl>
													{showView === true ? (
														<p className="text-black">
															{field.value
																? convertTimestamp(field.value)
																: '- -'}
														</p>
													) : (
														<DatePicker
															placeholder={'Enter deadline for this project'}
															date={field.value}
															setDate={(date) =>
																field.onChange(date?.toString())
															}
														/>
													)}
												</FormControl>
											</FormItem>
										);
									}}
								/>
							</div>
						</div>
						<div className="flex flex-col">
							<FormField
								control={form.control}
								name="agency"
								render={({ field }) => {
									return (
										<FormItem>
											<div className="flex justify-between">
												<Label className="font-bold">
													What Institution is this project for?
												</Label>
											</div>
											<FormControl>
												{renderInput(
													field,
													'Enter the name of the institution or agency funding this project'
												)}
											</FormControl>
											<FormMessage />
										</FormItem>
									);
								}}
							/>
						</div>
						{type === 'dialog' && (
							<div className="flex flex-col">
								<FormField
									control={form.control}
									name="summary"
									render={({ field }) => {
										return (
											<FormItem>
												<div className="flex justify-between">
													<Label className="font-bold">
														Summary{' '}
														<span className="text-secondary">(required)</span>
													</Label>
												</div>
												<FormControl>
													{showView === true ? (
														<>{renderText(field)}</>
													) : (
														<Textarea
															{...field}
															className={cn(
																'bg-white border-[#D3DDE2] h-12 rounded-xl hover:bg-[#EAF1FC] disabled:bg-[#F5F5F5] h-28 resize-none'
															)}
															placeholder="Write a summary here.."
														></Textarea>
													)}
												</FormControl>
												<FormMessage />
											</FormItem>
										);
									}}
								/>
							</div>
						)}
						{controlLayout}
					</CardContent>
				</Card>
			</form>
		</Form>
	);
};

export default ManualSolicitationSummary;
